import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import EmojiPicker from 'emoji-picker-react';
import { useAdmin } from '@/contexts/AdminContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import logger from '@/utils/logger';
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  FolderOpen,
  Palette,
  Check,
  X,
  RefreshCw,
  Tag
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRESET_COLORS = [
  '#EF4444', // Red
  '#F97316', // Orange
  '#EAB308', // Yellow
  '#22C55E', // Green
  '#14B8A6', // Teal
  '#3B82F6', // Blue
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#6366F1', // Indigo
  '#0EA5E9', // Sky
];

const PRESET_ICONS = [
  'heart', 'star', 'camera', 'plane', 'mountain', 'palmtree', 
  'compass', 'map', 'users', 'sparkles', 'sunset', 'tent'
];

const ThemeTourCategories = () => {
  const { getAuthHeaders } = useAdmin();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: '',
    color: '#EF4444',
    is_active: true
  });

  // Emoji picker state
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiPickerRef = useRef(null);

  // Close emoji picker on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target)) {
        setShowEmojiPicker(false);
      }
    };
    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmojiPicker]);

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/theme-tour-categories`, {
        headers: getAuthHeaders()
      });
      setCategories(response.data);
    } catch (error) {
      logger.error('Error fetching categories:', error);
      toast.error('Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      icon: '',
      color: '#EF4444',
      is_active: true
    });
    setEditingCategory(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      icon: category.icon || '',
      color: category.color || '#EF4444',
      is_active: category.is_active
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Please enter a category name');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      if (editingCategory) {
        await axios.put(
          `${API_URL}/api/admin/theme-tour-categories/${editingCategory.id}`,
          formData,
          { headers: getAuthHeaders() }
        );
        toast.success('Category updated successfully');
      } else {
        await axios.post(
          `${API_URL}/api/admin/theme-tour-categories`,
          formData,
          { headers: getAuthHeaders() }
        );
        toast.success('Category created successfully');
      }
      setShowModal(false);
      resetForm();
      fetchCategories();
    } catch (error) {
      logger.error('Error saving category:', error);
      toast.error(error.response?.data?.detail || 'Failed to save category');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (categoryId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/theme-tour-categories/${categoryId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Category deleted successfully');
      setDeleteConfirm(null);
      fetchCategories();
    } catch (error) {
      logger.error('Error deleting category:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  const toggleCategoryActive = async (category) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/theme-tour-categories/${category.id}`,
        { is_active: !category.is_active },
        { headers: getAuthHeaders() }
      );
      toast.success(`Category ${!category.is_active ? 'activated' : 'deactivated'}`);
      fetchCategories();
    } catch (error) {
      toast.error('Failed to update category status');
    }
  };

  const filteredCategories = categories.filter(cat =>
    cat.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (cat.description && cat.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="theme-tour-categories-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <FolderOpen className="h-6 w-6 text-red-500" />
            Theme Tour Categories
          </h1>
          <p className="text-slate-500">Organize theme tours into categories like &ldquo;Pre Wedding&rdquo;, &ldquo;Women Special&rdquo;, etc.</p>
        </div>
        <Button
          onClick={openCreateModal}
          className="bg-red-600 hover:bg-red-700"
          data-testid="add-category-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Category
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-categories"
            />
          </div>
        </CardContent>
      </Card>

      {/* Categories Grid */}
      {filteredCategories.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <FolderOpen className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-600 mb-2">No categories yet</h3>
            <p className="text-slate-500 mb-4">Create your first category to organize theme tours.</p>
            <Button onClick={openCreateModal} className="bg-red-600 hover:bg-red-700">
              <Plus className="w-4 h-4 mr-2" /> Create Category
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredCategories.map((category) => (
            <Card 
              key={category.id} 
              className={`relative overflow-hidden transition-shadow hover:shadow-lg ${!category.is_active ? 'opacity-60' : ''}`}
              data-testid={`category-card-${category.id}`}
            >
              {/* Color Bar */}
              <div 
                className="h-2" 
                style={{ backgroundColor: category.color || '#EF4444' }}
              />
              
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {category.icon ? (
                      <span className="text-2xl">{category.icon}</span>
                    ) : (
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${category.color}20` }}
                      >
                        <Tag className="w-5 h-5" style={{ color: category.color }} />
                      </div>
                    )}
                    <div>
                      <h3 className="font-semibold text-slate-800">{category.name}</h3>
                      <p className="text-xs text-slate-500">{category.slug}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => toggleCategoryActive(category)}
                    className={`px-2 py-1 text-xs rounded-full font-medium ${
                      category.is_active 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-red-100 text-red-700'
                    }`}
                  >
                    {category.is_active ? 'Active' : 'Inactive'}
                  </button>
                </div>
                
                {category.description && (
                  <p className="text-sm text-slate-600 mb-3 line-clamp-2">
                    {category.description}
                  </p>
                )}
                
                <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                  <span className="text-sm text-slate-500">
                    {category.tour_count || 0} tour{(category.tour_count || 0) !== 1 ? 's' : ''}
                  </span>
                  <div className="flex gap-1">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8"
                      onClick={() => openEditModal(category)}
                      data-testid={`edit-category-${category.id}`}
                    >
                      <Pencil className="w-4 h-4 text-blue-600" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8"
                      onClick={() => setDeleteConfirm(category)}
                      data-testid={`delete-category-${category.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingCategory ? 'Edit Category' : 'Create New Category'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Category Name *</Label>
              <Input
                id="name"
                placeholder="e.g., Pre Wedding, Women Special, Adventure"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="input-category-name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Brief description of this category..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                data-testid="input-category-description"
              />
            </div>
            
            {/* Color Selection */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Palette className="w-4 h-4" /> Category Color
              </Label>
              <div className="flex flex-wrap gap-2">
                {PRESET_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setFormData({ ...formData, color })}
                    className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${
                      formData.color === color ? 'border-slate-800 scale-110' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: color }}
                  >
                    {formData.color === color && (
                      <Check className="w-4 h-4 text-white mx-auto" />
                    )}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Label className="text-xs text-slate-500">Custom:</Label>
                <Input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="w-12 h-8 p-0 border-0"
                />
                <Input
                  type="text"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="w-24 h-8 text-xs font-mono"
                  placeholder="#000000"
                />
              </div>
            </div>
            
            {/* Icon (Emoji Picker) */}
            <div className="space-y-2">
              <Label>Icon (emoji)</Label>
              <div className="relative" ref={emojiPickerRef}>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShowEmojiPicker((v) => !v)}
                    className="w-12 h-10 rounded-md border border-slate-200 flex items-center justify-center text-xl hover:bg-slate-50 transition-colors"
                    data-testid="input-category-icon"
                  >
                    {formData.icon || <Tag className="w-5 h-5 text-slate-400" />}
                  </button>
                  <span className="text-sm text-slate-500">
                    {formData.icon ? 'Click to change' : 'Click to pick an emoji'}
                  </span>
                  {formData.icon && (
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, icon: '' })}
                      className="text-slate-400 hover:text-red-500 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {showEmojiPicker && (
                  <div className="absolute z-50 mt-1 left-0">
                    <EmojiPicker
                      onEmojiClick={(emojiData) => {
                        setFormData({ ...formData, icon: emojiData.emoji });
                        setShowEmojiPicker(false);
                      }}
                      width={320}
                      height={400}
                      searchPlaceholder="Search emoji..."
                      previewConfig={{ showPreview: false }}
                    />
                  </div>
                )}
              </div>
            </div>
            
            {/* Active Toggle */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <Label className="font-medium">Show on Website</Label>
                <p className="text-xs text-slate-500">
                  {formData.is_active ? 'Category will be visible' : 'Category will be hidden'}
                </p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                data-testid="toggle-category-active"
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-red-600 hover:bg-red-700"
                disabled={isSubmitting}
                data-testid="save-category-btn"
              >
                {isSubmitting ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  editingCategory ? 'Update Category' : 'Create Category'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Category</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            Are you sure you want to delete <strong>{deleteConfirm?.name}</strong>?
          </p>
          {deleteConfirm?.tour_count > 0 && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
              Warning: This category has {deleteConfirm.tour_count} tour(s) assigned. 
              You must reassign them before deleting.
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button 
              className="bg-red-600 hover:bg-red-700" 
              onClick={() => handleDelete(deleteConfirm?.id)}
              disabled={deleteConfirm?.tour_count > 0}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ThemeTourCategories;
